/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable @typescript-eslint/no-unused-vars */
// TODO: Probably should be moved to widgets - Maksym
import styles from "./ChatMessage.module.css";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import { memo, useEffect, useState, Fragment } from "react";
import { ChatMessage as ChatMessageType } from "@/zapi-frontend/stores/chatStore";
import { Bot, User, AlertCircle, Search, FileEdit, Check, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { Avatar, AvatarFallback } from "@libs/zapi-ui/shadcn";
import { parseMessageContent } from "./lib/parseMessageContent";
import { getMessageStyles } from "./lib/getMessageStyles";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { MagnifyingGlass } from "@phosphor-icons/react";

const findAction = (actions: ParsedContent["actions"], type: string) => actions?.find((a) => a.type === type);

export type ParsedContent = {
  type: "answer" | "websearch" | "error" | "loading" | "updateChapter" | "multiAction" | "agent";
  content: string;
  notes?: string[];
  agentType?: string;
  actions?: Array<{
    type: string;
    content: string;
    notes?: string[];
    agentType?: string;
  }>;
};

// Loading animation for assistant messages
const LoadingIndicator = ({ progressMessage }: { progressMessage?: string | null }) =>
  progressMessage ? (
    <div className={"flex flex-col " + styles.animatefadeIn}>
      <div className="flex items-center space-x-2 border-b border-gray-100 pb-2">
        <MagnifyingGlass size={16} className="text-blue-500" weight="bold" />
        <div className="flex items-end">
          <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Thinking</span>
          <div className="flex space-x-0.5 pb-1 ml-0">
            <div
              className="w-[2px] h-[2px] bg-blue-600 rounded-full animate-pulse"
              style={{ animationDelay: "0ms" }}
            ></div>
            <div
              className="w-[2px] h-[2px] bg-blue-600 rounded-full animate-pulse"
              style={{ animationDelay: "150ms" }}
            ></div>
            <div
              className="w-[2px] h-[2px] bg-blue-600 rounded-full animate-pulse"
              style={{ animationDelay: "300ms" }}
            ></div>
          </div>
        </div>
      </div>
      <div key={progressMessage} className={"text-base text-gray-700 pt-2 " + styles.animatefadeIn}>
        {progressMessage}
      </div>
    </div>
  ) : (
    <div className={"flex flex-col space-y-3 p-2 " + styles.animatefadeIn}>
      <div className="flex space-x-1 items-center">
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
        <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
      </div>
    </div>
  );

export const ChatMessage = memo(function ChatMessage({
  message,
  isLoading,
  progressMessage
}: {
  message: ChatMessageType;
  isLoading?: boolean;
  progressMessage?: string | null;
}) {
  const { isLabs } = useMeStore();
  const [parsedContent, setParsedContent] = useState<ParsedContent>({
    type: "loading",
    content: "Loading..."
  });

  const [isContentExpanded, setIsContentExpanded] = useState(false);
  const [parseError, setParseError] = useState(false);

  useEffect(() => {
    if (message.role === "user") {
      setParsedContent({ type: "answer", content: message.content });
      setParseError(false);
      return;
    }

    if (isLoading) {
      setParsedContent({ type: "loading", content: "Loading..." });
      setParseError(false);
      return;
    }

    try {
      const parsed = parseMessageContent(message.content, !!isLabs);
      setParsedContent(parsed);
      setParseError(false);
    } catch (error) {
      console.error("Critical error parsing message:", error);
      setParsedContent({
        type: "error",
        content: "There was a problem displaying this message. It may contain invalid formatting."
      });
      setParseError(true);
    }
  }, [message, isLoading, isLabs]);

  // If still loading
  if (isLoading) {
    return (
      <div className={`${styles.animatefadeIn} flex items-start gap-2 max-w-full text-base`}>
        <Avatar className="h-8 w-8 bg-primary/10">
          <AvatarFallback>
            <Bot size={16} />
          </AvatarFallback>
        </Avatar>
        <div
          className={`rounded-2xl shadow-sm px-4 py-3 max-w-[75%] ${
            progressMessage ? "bg-gray-50 border-l-[3px] border-l-blue-500" : "bg-gray-50 border border-gray-100"
          }`}
        >
          <LoadingIndicator progressMessage={progressMessage} />
        </div>
      </div>
    );
  }

  const isUser = message.role === "user";

  return (
    <div
      className={`${styles.animatefadeIn} flex items-start gap-2 max-w-full ${
        message.role === "user" ? "flex-row-reverse" : ""
      }`}
    >
      {/* Avatar for the message */}
      <Avatar className={`h-8 w-8`}>
        <AvatarFallback>{message.role === "user" ? <User size={16} /> : <Bot size={16} />}</AvatarFallback>
      </Avatar>

      {/* Message content */}
      <div className={`${getMessageStyles(message, parsedContent, parseError)}`}>
        {/* Render header conditionally based on type */}
        {parseError && (
          <div className="flex items-center gap-1 pb-1 mb-2 border-b border-gray-100">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-xs font-medium text-red-500">FORMAT ERROR</span>
          </div>
        )}
        {!isUser && !parseError && parsedContent.type === "websearch" && (
          <div className="flex items-center gap-1 pb-1 mb-2 border-b border-gray-100">
            <Search className="h-4 w-4 text-blue-500" />
            <span className="text-xs font-medium text-blue-500">WEB RESEARCH</span>
          </div>
        )}
        {!isUser && !parseError && parsedContent.type === "error" && (
          <div className="flex items-center gap-1 pb-1 mb-2 border-b border-gray-100">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-xs font-medium text-red-500">ERROR</span>
          </div>
        )}
        {!isUser && !parseError && parsedContent.type === "updateChapter" && (
          <div className="flex items-center gap-1 pb-2 mb-2 border-b border-gray-100">
            <FileEdit className="h-4 w-4 text-green-500" />
            <span className="text-xs font-medium text-green-500">CHAPTER UPDATED</span>
          </div>
        )}
        {!isUser && !parseError && parsedContent.type === "agent" && (
          <div className="flex items-center gap-1 pb-2 mb-2 border-b border-gray-100">
            <Sparkles className="h-4 w-4 text-purple-500" />
            <span className="text-xs font-medium text-purple-500">
              {/* Add check for agentType before accessing */}
              {parsedContent.agentType ? `${parsedContent.agentType.toUpperCase()} AGENT` : "SPECIALIZED AGENT"}
            </span>
          </div>
        )}

        {/* Message content (handles different types internally) */}
        <div
          className={`prose prose-sm max-w-none ${
            message.role === "user" ? "text-white prose-invert" : "text-gray-800"
          }`}
        >
          {/* Use a try-catch block to prevent rendering errors */}
          {(() => {
            // Specific rendering for fallback updateChapter notes
            if (!isUser && parsedContent.type === "updateChapter" && !parseError) {
              // Check parsedContent.notes before rendering NotesSection in fallback
              if (parsedContent.notes && parsedContent.notes.length > 0) {
                return (
                  <div className="mb-1">
                    {/* Render notes directly in fallback */}
                    <div className="text-xs font-medium text-gray-500 mb-2">Changes applied:</div>
                    <ul className="space-y-2 mb-3">
                      {/* Add optional chaining for map */}
                      {parsedContent.notes?.map((note, index) => (
                        <li key={index} className="flex items-start gap-2 text-gray-700">
                          <Check size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
                          <div className="prose prose-sm max-w-none">
                            <ReactMarkdown components={{ p: Fragment }}>{note}</ReactMarkdown>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              } else {
                return <div className="text-sm text-gray-700">Chapter details were updated.</div>;
              }
            }

            // Specific rendering for fallback agent notes
            if (!isUser && parsedContent.type === "agent" && !parseError) {
              return (
                <div className="mb-1">
                  {/* Render agent content first */}
                  <div className="prose prose-sm max-w-none text-gray-800 mb-3">
                    <ReactMarkdown>{parsedContent.content || ""}</ReactMarkdown>
                  </div>
                  {/* Then render notes if they exist */}
                  {parsedContent.notes && parsedContent.notes.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="text-xs font-medium text-gray-500 mb-2">Agent notes:</div>
                      <ul className="space-y-2">
                        {/* Add check for notes before mapping */}
                        {parsedContent.notes?.map((note, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                            <Check size={16} className="text-purple-500 flex-shrink-0 mt-0.5" />
                            <span>{note}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            }

            // Default Markdown rendering for other types (user, answer, websearch, error content)
            try {
              // Unescape quotes in content (same as project autosetup)
              // This ensures ReactMarkdown can parse the HTML correctly
              // After JSON.parse(), \\" becomes \", so we need to unescape \" to " right before rendering
              let contentToRender = parsedContent.content || "";
              if (parsedContent.type === "answer") {
                // Unescape quotes in the entire content
                contentToRender = contentToRender.replace(/\\"/g, '"');
              }

              return (
                // TODO: Can we avoid linting problems here?
                <ReactMarkdown
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    a: ({ node, ...props }) => (
                      // eslint-disable-next-line jsx-a11y/anchor-has-content
                      <a
                        {...props}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`underline ${message.role === "user" ? "text-blue-100" : "text-blue-500"}`}
                      />
                    ),
                    p: ({ node, ...props }) => <p {...props} className="mb-3 last:mb-0" />,
                    ul: ({ node, ...props }) => <ul {...props} className="list-disc pl-5 mb-3" />,
                    ol: ({ node, ...props }) => <ol {...props} className="list-decimal pl-5 mb-3" />,
                    li: ({ node, ...props }) => <li {...props} className="mb-1" />,
                    h1: ({ node, ...props }) => <h1 {...props} className="text-xl font-bold mb-2" />,
                    h2: ({ node, ...props }) => <h2 {...props} className="text-lg font-bold mb-2" />,
                    h3: ({ node, ...props }) => <h3 {...props} className="text-md font-bold mb-2" />
                  }}
                >
                  {contentToRender}
                </ReactMarkdown>
              );
            } catch (error) {
              // Fallback if markdown rendering fails
              console.error("Error rendering markdown:", error);
              return (
                <div className="text-red-500 p-2">
                  Failed to render message content. The message may contain invalid formatting.
                  <pre className="mt-2 p-2 bg-red-50 text-xs overflow-auto max-h-32">
                    {typeof parsedContent.content === "string"
                      ? parsedContent.content.substring(0, 500)
                      : "Invalid content"}
                  </pre>
                </div>
              );
            }
          })()}
        </div>
      </div>
    </div>
  );
});
